import React, { Suspense } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { AdminProvider } from './context/AdminContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Admin } from './pages/Admin';
import { AdminGuard } from './components/AdminGuard';
import { Quote } from './pages/Quote';
import { Bike, Settings, ArrowLeft, UserCheck, Loader2, User, LogOut } from 'lucide-react';

// Lazy loading da Área do Cliente para máxima performance
const ClientArea = React.lazy(() => import('./pages/ClientArea'));

function LoadingFallback() {
  return (
    <div className="min-h-[450px] flex flex-col items-center justify-center p-8 bg-white/60 rounded-3xl border border-slate-100 shadow-sm text-center">
      <Loader2 size={36} className="text-brand-blue animate-spin mb-3" />
      <p className="font-extrabold text-slate-800 text-sm">Carregando Área do Cliente...</p>
      <p className="text-xs text-slate-400 mt-1">Carregamento otimizado de componentes</p>
    </div>
  );
}

function Navbar() {
  const location = useLocation();
  const { user, perfil, isOperator, logout } = useAuth();
  const isAdmin = location.pathname === '/admin';
  const isCliente = location.pathname === '/cliente';

  return (
    <nav className="sticky top-0 z-50 glassmorphism border-b border-white/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center">
        <div className="flex items-center gap-4">

          {/* Botão Voltar — só aparece no mobile */}
          <a
            href="/"
            className="flex md:hidden items-center justify-center w-9 h-9 rounded-full bg-brand-blue/10 hover:bg-brand-blue/20 text-brand-blue transition-colors"
            aria-label="Voltar ao site principal"
          >
            <ArrowLeft size={18} />
          </a>

          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-brand-green to-emerald-700 flex items-center justify-center text-white text-xs font-black shadow-lg shadow-brand-green/25 group-hover:scale-105 transition-transform">
              ABH
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-extrabold text-brand-navy text-sm tracking-wide uppercase">ABH Motoboys</span>
              <span className="text-[10px] text-slate-400 font-medium">Logística Inteligente BH</span>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center gap-4 text-sm font-semibold text-slate-600">
            <a href="/" className="hover:text-brand-blue transition-colors">Início</a>
            <Link to="/" className={`hover:text-brand-blue transition-colors ${location.pathname === '/' ? 'text-brand-green font-bold' : ''}`}>Cotação Rápida</Link>
            <Link to="/cliente" className={`flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${isCliente ? 'bg-brand-blue text-white shadow-md shadow-brand-blue/20' : 'bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/20'}`}>
              <UserCheck size={14} /> {user ? `Área do Cliente (${perfil?.nome?.split(' ')[0] || 'Logado'})` : 'Área do Cliente (Login)'}
            </Link>
            <a href="/#servicos" className="hover:text-brand-blue transition-colors">Serviços</a>
            <a href="/faq.html" className="hover:text-brand-blue transition-colors">FAQ</a>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link to="/cliente" className="md:hidden flex items-center gap-1 text-xs font-bold text-brand-blue bg-brand-blue/10 px-2.5 py-1.5 rounded-lg">
            <UserCheck size={14} /> {user ? 'Meu Painel' : 'Entrar'}
          </Link>

          {isAdmin ? (
            <Link to="/" className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-blue transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-50">
              <ArrowLeft size={14} /> Voltar
            </Link>
          ) : (isOperator || !isCliente) ? (
            <Link to="/admin" className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-brand-blue transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-50">
              <Settings size={12} /> Admin
            </Link>
          ) : null}
        </div>
      </div>
    </nav>
  );
}

function App() {
  return (
    <AuthProvider>
      <AdminProvider>
        <Router>
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 font-sans">
            <Navbar />
            <main className="px-4 sm:px-8 py-4 sm:py-6 max-w-7xl mx-auto">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<Quote />} />
                  <Route path="/cliente" element={<ClientArea />} />
                  <Route path="/admin" element={
                    <AdminGuard>
                      <Admin />
                    </AdminGuard>
                  } />
                </Routes>
              </Suspense>
            </main>
          </div>
        </Router>
      </AdminProvider>
    </AuthProvider>
  );
}

export default App;


